'use client'

import * as React from 'react'
import { tw } from 'utils/classnames'
import { LFS_URL } from '@web/utils/env'
import {
  CARTOON_LOADER_CLIP_COUNT,
  CARTOON_LOADER_SMALL_SCREEN_QUERY,
  advanceCartoonLoaderPlaylist,
  cartoonLoaderClipUrls,
  createCartoonLoaderPlaylist,
  currentCartoonLoaderClip,
  peekNextCartoonLoaderClip,
  preferredCartoonLoaderFormat,
} from './cartoon-loader-playlist'
import type { CartoonLoaderSize } from './cartoon-loader-playlist'

function subscribeToNothing() {
  return () => undefined
}

function useCartoonLoaderFormat() {
  return React.useSyncExternalStore(
    subscribeToNothing,
    () => preferredCartoonLoaderFormat(navigator.userAgent),
    () => null,
  )
}

function subscribeToScreenSize(onChange: () => void) {
  const query = window.matchMedia(CARTOON_LOADER_SMALL_SCREEN_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function useCartoonLoaderSize(): CartoonLoaderSize {
  return React.useSyncExternalStore(
    subscribeToScreenSize,
    () => (window.matchMedia(CARTOON_LOADER_SMALL_SCREEN_QUERY).matches ? '1x' : '3x'),
    () => '3x',
  )
}

function playVideo(element: HTMLVideoElement | null) {
  if (!element) {
    return
  }
  void element.play().catch(() => undefined)
}

export function CartoonLoader({
  clips,
  random = Math.random,
}: {
  clips?: readonly string[]
  random?: () => number
}) {
  const format = useCartoonLoaderFormat()
  const size = useCartoonLoaderSize()
  const clipUrls = clips ?? (format ? cartoonLoaderClipUrls(LFS_URL ?? '', format, size) : [])
  const [playlist, setPlaylist] = React.useState(() =>
    createCartoonLoaderPlaylist(clips?.length ?? CARTOON_LOADER_CLIP_COUNT, random),
  )
  const [activeSlot, setActiveSlot] = React.useState<0 | 1>(0)
  const firstSlot = React.useRef<HTMLVideoElement>(null)
  const secondSlot = React.useRef<HTMLVideoElement>(null)

  const currentSrc = clipUrls[currentCartoonLoaderClip(playlist)] ?? clipUrls[0]
  const nextSrc = clipUrls[peekNextCartoonLoaderClip(playlist)] ?? clipUrls[0]

  React.useEffect(() => {
    playVideo(activeSlot === 0 ? firstSlot.current : secondSlot.current)
  }, [activeSlot, currentSrc])

  function handleEnded() {
    setPlaylist(previous => advanceCartoonLoaderPlaylist(previous, random))
    setActiveSlot(slot => (slot === 0 ? 1 : 0))
  }

  return (
    <div className="relative size-full overflow-hidden" aria-hidden>
      <video
        ref={firstSlot}
        src={activeSlot === 0 ? currentSrc : nextSrc}
        muted
        playsInline
        preload="auto"
        className={tw(
          'size-full object-contain',
          activeSlot === 0 ? 'relative' : 'invisible absolute inset-0',
        )}
        onEnded={activeSlot === 0 ? handleEnded : undefined}
      />
      <video
        ref={secondSlot}
        src={activeSlot === 1 ? currentSrc : nextSrc}
        muted
        playsInline
        preload="auto"
        className={tw(
          'size-full object-contain',
          activeSlot === 1 ? 'relative' : 'invisible absolute inset-0',
        )}
        onEnded={activeSlot === 1 ? handleEnded : undefined}
      />
    </div>
  )
}
